/**
 * Cloud storage connections.
 *
 * What this replaces: a hardcoded list of two "connected" providers with
 * invented sync times. Nothing was connected and nothing synced.
 *
 * Microsoft reads app-only through the Entra application already registered
 * for mail, so there is nothing for a person to sign into — pick a SharePoint
 * library or a OneDrive folder and it is connected. Google Drive reads as the
 * person who connects it, so it stays honestly marked unavailable until this
 * deployment has a Google OAuth client.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Cloud,
  FolderOpen,
  Link2,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import clsx from 'clsx'

import api from '@/services/api'

type ProviderStatus = {
  provider: string
  label: string
  auth_mode: string
  ok: boolean
  status: string
  detail: string
  setup: string
}

type Connection = {
  id: string
  provider: string
  display_name: string
  target: Record<string, string>
  loan_id: string | null
  default_category: string
  auth_mode: string
  connected_by: string | null
  enabled: boolean
  status: string
  status_detail: string | null
  last_sync_at: string | null
  last_sync_files: number
  file_count: number
}

type Site = { id: string; name: string; url: string }
type Drive = { id: string; name: string; url: string; kind: string }
type Item = { id: string; name: string; is_folder: boolean; child_count: number | null }

const statusTone: Record<string, string> = {
  connected: 'text-emerald-400',
  needs_reauth: 'text-amber-300',
  needs_consent: 'text-amber-300',
  not_configured: 'text-stone-500',
  error: 'text-red-300',
}

function when(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString()
}

export default function CloudStorageTab() {
  const queryClient = useQueryClient()
  const [picking, setPicking] = useState(false)

  const providers = useQuery({
    queryKey: ['cloud-storage', 'providers'],
    queryFn: async () => (await api.get<ProviderStatus[]>('/cloud-storage/providers')).data,
    staleTime: 60_000,
  })

  const connections = useQuery({
    queryKey: ['cloud-storage', 'connections'],
    queryFn: async () => (await api.get<Connection[]>('/cloud-storage')).data,
    staleTime: 15_000,
  })

  const sync = useMutation({
    mutationFn: (id: string) => api.post(`/cloud-storage/${id}/sync`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cloud-storage'] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/cloud-storage/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cloud-storage'] }),
  })

  const microsoftReady = (providers.data ?? []).some((p) => p.auth_mode === 'app_only' && p.ok)

  return (
    <div className="space-y-4">
      {/* Providers */}
      <div className="grid gap-2 lg:grid-cols-3">
        {(providers.data ?? []).map((p) => (
          <div key={p.provider} className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
            <div className="flex items-center gap-2">
              <Cloud size={13} className="text-amber-400/70" />
              <span className="text-[12px] font-medium text-stone-100">{p.label}</span>
              <span className={clsx('ml-auto text-[10px]', statusTone[p.status] ?? 'text-stone-500')}>
                {p.ok ? 'Available' : p.status.replace(/_/g, ' ')}
              </span>
            </div>
            <p className="mt-1 text-[10px] text-stone-500">{p.detail}</p>
            {!p.ok ? (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[10px] text-amber-300/80 hover:text-amber-200">
                  What it needs
                </summary>
                <p className="mt-1 text-[10px] leading-relaxed text-stone-500">{p.setup}</p>
              </details>
            ) : (
              <p className="mt-1 text-[10px] text-stone-600">
                Reads under the platform's own application — nobody signs in.
              </p>
            )}
          </div>
        ))}
        {providers.isPending ? (
          <div className="flex items-center gap-2 px-1 py-2 text-[11px] text-stone-500">
            <Loader2 size={12} className="motion-safe:animate-spin" /> Checking providers…
          </div>
        ) : null}
      </div>

      {/* Connections */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Connected folders</span>
          <button
            type="button"
            disabled={!microsoftReady}
            onClick={() => setPicking(true)}
            title={
              microsoftReady
                ? 'Point the vault at a SharePoint library or OneDrive folder'
                : 'Microsoft file access is not available yet — see the provider card above'
            }
            className="ml-auto flex items-center gap-1 rounded border border-amber-400/25 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
          >
            <Link2 size={10} /> Connect a folder
          </button>
        </div>

        {connections.isPending ? (
          <div className="flex items-center gap-2 px-4 py-6 text-[11px] text-stone-500">
            <Loader2 size={12} className="motion-safe:animate-spin" /> Reading connections…
          </div>
        ) : (connections.data ?? []).length === 0 ? (
          <div className="px-4 py-8 text-center text-[11px] italic text-stone-500">
            Nothing connected. A connected folder is swept into the Document Vault and filed under
            its deal, the same as an emailed attachment.
          </div>
        ) : (
          <table className="dense-table">
            <thead>
              <tr>
                <th>Folder</th>
                <th>Provider</th>
                <th>Files</th>
                <th>Last sync</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(connections.data ?? []).map((c) => (
                <tr key={c.id}>
                  <td className="font-medium text-stone-200">{c.display_name}</td>
                  <td className="text-[10px] uppercase tracking-wider text-stone-400">
                    {c.provider.replace('_', ' ')}
                  </td>
                  <td className="font-mono text-[10px] text-stone-400">{c.file_count}</td>
                  <td className="text-[10px] text-stone-500">
                    {when(c.last_sync_at)}
                    {c.last_sync_at ? ` · ${c.last_sync_files} new` : ''}
                  </td>
                  <td>
                    <span className={clsx('text-[10px]', statusTone[c.status] ?? 'text-stone-400')} title={c.status_detail ?? undefined}>
                      {c.status === 'connected' ? (
                        <span className="inline-flex items-center gap-1">
                          <Check size={10} /> Connected
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <AlertTriangle size={10} /> {c.status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => sync.mutate(c.id)}
                        disabled={sync.isPending}
                        title="Pull anything new into the vault"
                        className="text-stone-500 hover:text-amber-300 disabled:opacity-40"
                      >
                        <RefreshCw
                          size={11}
                          className={sync.isPending && sync.variables === c.id ? 'animate-spin motion-reduce:animate-none' : undefined}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove.mutate(c.id)}
                        title="Forget this connection. Files already in the vault stay there."
                        className="text-stone-500 hover:text-red-300"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {sync.isError ? (
          <div className="px-3 py-2 text-[10px] text-red-300">
            {String((sync.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'The sync failed.')}
          </div>
        ) : null}
      </div>

      {picking ? <FolderPicker onClose={() => setPicking(false)} /> : null}
    </div>
  )
}

/** Site → library → folder, then connect. Three steps because that is how
 *  SharePoint is actually shaped; OneDrive skips the first. */
function FolderPicker({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'sharepoint' | 'onedrive'>('sharepoint')
  const [siteQuery, setSiteQuery] = useState('')
  const [site, setSite] = useState<Site | null>(null)
  const [upn, setUpn] = useState('')
  const [drive, setDrive] = useState<Drive | null>(null)
  const [path, setPath] = useState<Item[]>([])
  const [error, setError] = useState<string | null>(null)

  const sites = useQuery({
    queryKey: ['cloud-storage', 'sites', siteQuery],
    queryFn: async () => (await api.get<Site[]>('/cloud-storage/browse/sites', { params: { q: siteQuery } })).data,
    enabled: mode === 'sharepoint' && !site,
  })

  const drives = useQuery({
    queryKey: ['cloud-storage', 'drives', site?.id],
    queryFn: async () => (await api.get<Drive[]>(`/cloud-storage/browse/sites/${site!.id}/drives`)).data,
    enabled: Boolean(site) && !drive,
  })

  const items = useQuery({
    queryKey: ['cloud-storage', 'items', drive?.id, (path.length ? path[path.length - 1].id : null)],
    queryFn: async () =>
      (
        await api.get<Item[]>(`/cloud-storage/browse/drives/${drive!.id}/items`, {
          params: { item_id: (path.length ? path[path.length - 1].id : undefined) },
        })
      ).data,
    enabled: Boolean(drive),
  })

  const connect = useMutation({
    mutationFn: () =>
      api.post('/cloud-storage', {
        provider: mode,
        display_name: [site?.name, drive?.name, ...path.map((p) => p.name)].filter(Boolean).join(' / '),
        target: { driveId: drive!.id, itemId: (path.length ? path[path.length - 1].id : undefined) },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cloud-storage'] })
      onClose()
    },
    onError: (e: unknown) =>
      setError(
        String((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not connect.'),
      ),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Connect a cloud folder"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl border border-amber-300/15 bg-[#0d0c0b] shadow-2xl">
        <div className="border-b border-amber-300/10 px-4 py-3">
          <div className="text-[9px] uppercase tracking-[0.16em] text-amber-200/70">Connect a folder</div>
          <div className="mt-1 flex gap-1.5">
            {(['sharepoint', 'onedrive'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m)
                  setSite(null)
                  setDrive(null)
                  setPath([])
                }}
                className={clsx(
                  'rounded border px-2 py-0.5 text-[10px]',
                  mode === m
                    ? 'border-amber-400/40 bg-amber-400/[0.10] text-amber-200'
                    : 'border-white/[0.08] text-stone-400 hover:text-stone-200',
                )}
              >
                {m === 'sharepoint' ? 'SharePoint' : 'OneDrive'}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 scrollbar-thin">
          {/* Breadcrumb */}
          {(site || drive) && (
            <div className="mb-2 flex flex-wrap items-center gap-1 text-[10px] text-stone-500">
              {site ? <button type="button" onClick={() => { setDrive(null); setPath([]) }} className="hover:text-amber-200">{site.name}</button> : null}
              {drive ? (
                <>
                  <ChevronRight size={9} />
                  <button type="button" onClick={() => setPath([])} className="hover:text-amber-200">{drive.name}</button>
                </>
              ) : null}
              {path.map((p, i) => (
                <span key={p.id} className="flex items-center gap-1">
                  <ChevronRight size={9} />
                  <button type="button" onClick={() => setPath(path.slice(0, i + 1))} className="hover:text-amber-200">
                    {p.name}
                  </button>
                </span>
              ))}
            </div>
          )}

          {mode === 'sharepoint' && !site ? (
            <>
              <input
                value={siteQuery}
                onChange={(e) => setSiteQuery(e.target.value)}
                placeholder="Search SharePoint sites…"
                className="mb-2 w-full rounded border border-amber-300/15 bg-black/50 px-2 py-1 text-[11px] text-stone-200 outline-none focus:border-amber-400/40"
              />
              <PickList
                loading={sites.isPending}
                error={sites.error}
                rows={(sites.data ?? []).map((s) => ({ id: s.id, name: s.name, hint: s.url, onPick: () => setSite(s) }))}
                empty="No sites matched."
              />
            </>
          ) : mode === 'onedrive' && !drive ? (
            <>
              <input
                value={upn}
                onChange={(e) => setUpn(e.target.value)}
                placeholder="person@helios-fintech.com"
                className="mb-2 w-full rounded border border-amber-300/15 bg-black/50 px-2 py-1 text-[11px] text-stone-200 outline-none focus:border-amber-400/40"
              />
              <button
                type="button"
                disabled={!upn.includes('@')}
                onClick={async () => {
                  setError(null)
                  try {
                    const res = await api.get<Drive>('/cloud-storage/browse/user-drive', { params: { upn } })
                    setDrive(res.data)
                  } catch (e: unknown) {
                    setError(
                      String(
                        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
                          'That OneDrive could not be opened.',
                      ),
                    )
                  }
                }}
                className="rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
              >
                Open OneDrive
              </button>
            </>
          ) : !drive ? (
            <PickList
              loading={drives.isPending}
              error={drives.error}
              rows={(drives.data ?? []).map((d) => ({ id: d.id, name: d.name, hint: 'Document library', onPick: () => setDrive(d) }))}
              empty="This site has no document libraries."
            />
          ) : (
            <PickList
              loading={items.isPending}
              error={items.error}
              rows={(items.data ?? [])
                .filter((i) => i.is_folder)
                .map((i) => ({
                  id: i.id,
                  name: i.name,
                  hint: i.child_count != null ? `${i.child_count} items` : 'Folder',
                  onPick: () => setPath([...path, i]),
                }))}
              empty="No sub-folders here. Connect this folder to take everything in it."
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-amber-300/10 px-4 py-3">
          <div className="min-w-0 text-[10px] text-red-300">{error}</div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button type="button" onClick={onClose} className="rounded px-3 py-1 text-[11px] text-stone-400 hover:text-stone-200">
              Cancel
            </button>
            <button
              type="button"
              disabled={!drive || connect.isPending}
              onClick={() => connect.mutate()}
              className="inline-flex items-center gap-1.5 rounded border border-amber-400/30 bg-amber-400/[0.12] px-3 py-1 text-[11px] text-amber-200 hover:bg-amber-400/[0.2] disabled:opacity-50"
            >
              {connect.isPending ? <Loader2 size={11} className="motion-safe:animate-spin" /> : <FolderOpen size={11} />}
              Connect this folder
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function PickList({
  loading,
  error,
  rows,
  empty,
}: {
  loading: boolean
  error: unknown
  rows: { id: string; name: string; hint?: string; onPick: () => void }[]
  empty: string
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-[11px] text-stone-500">
        <Loader2 size={12} className="motion-safe:animate-spin" /> Reading…
      </div>
    )
  }
  if (error) {
    return (
      <div className="py-4 text-[11px] text-red-300">
        {String((error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Microsoft refused that request.')}
      </div>
    )
  }
  if (rows.length === 0) {
    return <div className="py-4 text-[11px] italic text-stone-500">{empty}</div>
  }
  return (
    <div className="space-y-0.5">
      {rows.map((row) => (
        <button
          key={row.id}
          type="button"
          onClick={row.onPick}
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-white/[0.04]"
        >
          <FolderOpen size={11} className="flex-shrink-0 text-amber-300/70" />
          <span className="min-w-0 flex-1 truncate text-[11px] text-stone-200">{row.name}</span>
          {row.hint ? <span className="flex-shrink-0 truncate text-[9px] text-stone-600">{row.hint}</span> : null}
          <ChevronRight size={10} className="flex-shrink-0 text-stone-600" />
        </button>
      ))}
    </div>
  )
}
